import { forwardRef, KeyboardEvent } from 'react'
import { useTranslation } from 'libs/i18n'
import { format } from 'date-fns'
import { CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { cn } from 'utils/style'
import { DATE_FORMAT, getDateLocale } from '../utils/date'

export type DatePickerVariant = 'outline' | 'form'

type CustomDatePickerButtonProps = {
	value?: any
	onClick?: any
	onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
	className?: string
	disabled?: boolean
	hasClearButton?: boolean
	hasError?: boolean
	isOpen?: boolean
	isRange?: boolean
	startDate: Date | undefined
	endDate?: Date | undefined
	placeholder?: string
	variant?: DatePickerVariant
}

export const CustomDatePickerButton = forwardRef(
	(
		{
			value,
			onClick,
			onKeyDown,
			className,
			startDate,
			endDate,
			placeholder,
			disabled,
			hasClearButton = false,
			hasError = false,
			isOpen = false,
			isRange = false,
			variant = 'form'
		}: CustomDatePickerButtonProps,
		ref: any
	) => {
		const { t, i18n } = useTranslation()
		const locale = getDateLocale(i18n.language)
		const hasValue = !!(value && startDate)
		const isForm = variant === 'form'

		const formatDate = (date: Date | undefined) =>
			date ? format(date, DATE_FORMAT, { locale }) : '…'

		const getLabel = () => {
			if (!hasValue) return placeholder ?? t('Select a date')

			return isRange ? `${formatDate(startDate)} – ${formatDate(endDate)}` : formatDate(startDate)
		}

		const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
			if (event.key !== 'Enter') onKeyDown?.(event)
		}

		return (
			<button
				type="button"
				className={cn(
					'flex w-full items-center gap-2.5 rounded-lg border border-border bg-card text-left text-[13px] transition hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-muted disabled:hover:border-border',
					isForm ? 'h-10 px-3' : 'h-9 px-2.5',
					hasError && 'border-danger hover:border-danger focus:ring-danger/20',
					isOpen && 'border-primary ring-2 ring-primary/20 hover:border-primary',
					hasClearButton && (isForm ? 'pr-10' : 'pr-9'),
					className
				)}
				disabled={disabled}
				onClick={onClick}
				onKeyDown={handleKeyDown}
				ref={ref}>
				<CalendarDaysIcon
					className={cn(
						'h-4 w-4 shrink-0 stroke-2',
						hasValue && !disabled ? 'text-primary' : 'text-muted-foreground'
					)}
				/>
				<span
					className={cn(
						'min-w-0 flex-1 truncate',
						hasValue ? 'font-medium text-foreground' : 'text-muted-foreground'
					)}>
					{getLabel()}
				</span>
			</button>
		)
	}
)

type DatePickerClearButtonProps = {
	label: string
	variant: DatePickerVariant
	onClick: () => void
}

export const DatePickerClearButton = ({ label, variant, onClick }: DatePickerClearButtonProps) => (
	<button
		type="button"
		aria-label={label}
		className={cn(
			'pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground opacity-0 transition hover:pointer-events-auto hover:bg-muted hover:text-foreground hover:opacity-100 focus:pointer-events-auto focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 peer-hover:pointer-events-auto peer-hover:opacity-100',
			variant === 'form' ? 'right-3' : 'right-2.5'
		)}
		onClick={onClick}>
		<XMarkIcon className="h-3.5 w-3.5 stroke-2" />
	</button>
)
